import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { Brain, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhoneInput, isValidE164 } from "@/components/PhoneInput";
import { toast } from "sonner";
import InstallAlphaCorePrompt from "@/components/InstallAlphaCorePrompt";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [country, setCountry] = useState(user?.phone_country || "");
  const [saving, setSaving] = useState(false);

  const savePhone = async () => {
    if (phone && !isValidE164(phone)) return toast.error("Enter a valid international phone number");
    setSaving(true);
    try {
      await api.patch("/me/profile", { phone, phone_country: country });
      toast.success("Phone number saved");
      refreshUser();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="kr-overline">[ SETTINGS ]</div>
      <h1 className="font-display text-4xl mt-2 tracking-tight">Profile</h1>

      <div className="mt-8 kr-card p-6 space-y-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#8A52FF] flex items-center justify-center text-xl font-semibold text-black overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || "?").toUpperCase()}
          </div>
          <div>
            <div className="font-display text-xl">{user?.name}</div>
            <div className="text-sm text-white/50">{user?.email}</div>
          </div>
        </div>
        <div className="border-t border-white/5 pt-5 grid grid-cols-3 gap-4">
          <div>
            <div className="kr-overline">Plan</div>
            <div className="text-sm mt-1 capitalize">{user?.plan}</div>
          </div>
          <div>
            <div className="kr-overline">Credits</div>
            <div className="text-sm mt-1">{user?.credits?.toLocaleString()}</div>
          </div>
          <div>
            <div className="kr-overline">Role</div>
            <div className="text-sm mt-1 capitalize">{user?.role}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 kr-card p-6">
        <div className="kr-overline">[ PHONE NUMBER ]</div>
        <p className="text-sm text-white/50 mt-2">Required for plan applications and payment verification. Stored in international format.</p>
        <div className="mt-4 max-w-md">
          <PhoneInput value={phone} onChange={(full, name) => { setPhone(full); setCountry(name); }} testid="settings-phone" />
          <Button onClick={savePhone} disabled={saving} className="mt-3 h-10 rounded-xl font-semibold text-white border-0" style={{ background: "linear-gradient(90deg,#FF3AA8,#8A52FF)" }} data-testid="settings-phone-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save phone number"}
          </Button>
          {user?.phone && <div className="text-xs text-white/35 mt-2">Current: {user.phone}{user.phone_country ? ` (${user.phone_country})` : ""}</div>}
        </div>
      </div>

      <Link to="/settings/memory" className="mt-6 kr-card p-6 flex items-center gap-4 hover:bg-white/[0.02] transition-colors" data-testid="settings-memory-link">
        <div className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center">
          <Brain className="h-5 w-5" style={{ color: "rgb(var(--ac-purple))" }} />
        </div>
        <div className="flex-1">
          <div className="kr-overline" style={{ color: "rgb(var(--ac-purple))" }}>[ ALPHACORE AI ]</div>
          <div className="font-display text-xl mt-1">Memory</div>
          <div className="text-sm text-white/50 mt-1">Manage what AlphaCore AI remembers, pause learning, export or delete everything.</div>
        </div>
        <ChevronRight className="h-5 w-5 text-white/30" />
      </Link>

      <div className="mt-6">
        <InstallAlphaCorePrompt variant="settings" />
      </div>

      <div className="mt-6 kr-card p-6">
        <div className="kr-overline">[ COMING SOON ]</div>
        <div className="text-sm text-white/60 mt-2">Email preferences, two-factor authentication, team members and API keys.</div>
      </div>
    </div>
  );
}
